'use client';

import { ShieldCheck, ArrowRight, Home } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function NotFound() {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const loadParticles = async () => {
      if (typeof window !== 'undefined' && (window as any).particlesJS) {
        (window as any).particlesJS('particles-js', {
          particles: {
            number: { value: 40, density: { enable: true, value_area: 800 } },
            color: { value: '#f43f5e' },
            shape: { type: 'circle' },
            opacity: { value: 0.3, random: false },
            size: { value: 2, random: true },
            line_linked: { enable: true, distance: 200, color: '#f43f5e', opacity: 0.8, width: 1.5 },
            move: { enable: true, speed: 1.5, direction: 'none', random: false, straight: false, out_mode: 'out', bounce: false }
          },
          interactivity: {
            detect_on: 'canvas',
            events: { onhover: { enable: true, mode: 'repulse' }, onclick: { enable: true, mode: 'push' }, resize: true },
            modes: { grab: { distance: 400, line_linked: { opacity: 1 } }, bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 }, repulse: { distance: 200, duration: 0.4 }, push: { particles_nb: 4 }, remove: { particles_nb: 2 } }
          },
          retina_detect: true
        });
      }
    };

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js';
    script.async = true;
    script.onload = () => loadParticles();
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return (
    <main className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Particles */}
      <div className="absolute inset-0 w-full h-full -z-10 opacity-50">
        <div id="particles-js" className="absolute inset-0"></div>
      </div>
      
      {/* Subtle Background Grid/Glow for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,#f43f5e0a,transparent)] pointer-events-none"></div>

      <div className="relative z-10 max-w-4xl w-full text-center flex flex-col items-center">
        
        {/* Badge */}
        <div className="flex sm:gap-3 bg-white/5 w-max rounded-full mx-auto pt-1.5 pr-3 pb-1.5 pl-3 gap-x-2 gap-y-2 items-center justify-center mb-8" style={{ position: 'relative', '--border-gradient': 'linear-gradient(135deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))', '--border-radius-before': '9999px' } as React.CSSProperties}>
          <ShieldCheck className="text-[18px] text-rose-500" />
          <span className="text-sm text-neutral-300">
          404 Error
          </span>
        </div>

        {/* Main Heading */}
        <h1 className="sm:text-7xl text-5xl font-semibold tracking-tight text-center mt-2 mb-6 max-w-3xl mx-auto leading-normal pb-1 text-white" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)', opacity: 1, transform: 'translateY(0px)', textShadow: '-2px 0 15px rgba(0, 0, 0, 0.9), 2px 0 15px rgba(0, 0, 0, 0.9), 0 2px 20px rgba(0, 0, 0, 0.8), 0 4px 40px rgba(0, 0, 0, 0.7), 0 0 60px rgba(0, 0, 0, 0.5)' }}>
          Page Not Found
        </h1>

        {/* Subtitle */}
        <p className="sm:text-lg text-base text-neutral-400 text-center mt-2 mb-12 max-w-xl mx-auto">
          The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-6 items-center justify-center">
            {/* Primary Button */}
            <Link
            href="/"
            className="inline-flex transition overflow-hidden group text-sm font-medium text-white rounded-full pt-3 pr-8 pb-3 pl-8 relative gap-x-2 gap-y-2 items-center cursor-pointer"
            style={{
              '--x': `${mousePos.x}%`,
              '--y': `${mousePos.y}%`,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: 'rgba(0, 0, 0, 0.3) 0px 8px 32px, rgba(255, 255, 255, 0.4) 0px 2px 4px inset, rgba(0, 0, 0, 0.2) 0px -2px 4px inset, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset',
              backdropFilter: 'blur(20px)',
              transformStyle: 'preserve-3d',
              transition: '0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(15px)'
            } as React.CSSProperties}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              setMousePos({ x, y });
              const centerX = rect.width / 2;
              const centerY = rect.height / 2;
              const rotateX = ((e.clientY - rect.top - centerY) / centerY) * -15;
              const rotateY = ((e.clientX - rect.left - centerX) / centerX) * 15;
              e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(25px)`;
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.4), inset 0 3px 6px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.2) inset, 0 0 60px rgba(244,63,94,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(15px)';
              e.currentTarget.style.boxShadow = 'rgba(0, 0, 0, 0.3) 0px 8px 32px, rgba(255, 255, 255, 0.4) 0px 2px 4px inset, rgba(0, 0, 0, 0.2) 0px -2px 4px inset, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset';
            }}
          >
            <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.05) 100%)', pointerEvents: 'none', zIndex: 1 }}></div>
            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle at var(--x) var(--y), rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 40%, transparent 70%)', opacity: 0.6, transition: 'opacity 0.5s', pointerEvents: 'none', zIndex: 2 }}></div>
            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(ellipse at var(--x) var(--y), rgba(244,63,94,0.4) 0%, rgba(251,113,133,0.2) 30%, transparent 60%)', opacity: 0.4, transition: 'opacity 0.5s', pointerEvents: 'none', filter: 'blur(8px)', zIndex: 3 }}></div>
            <Home className="w-4 h-4 relative z-10" />
            <span className="z-10 relative" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4), 0 0 8px rgba(255,255,255,0.2)' }}>
              Return Home
            </span>
          </Link>

          {/* Secondary Button */}
          <Link
            href="/docs"
            className="inline-flex transition overflow-hidden group text-sm font-medium text-white/70 hover:text-white rounded-full pt-3 pr-5 pb-3 pl-5 relative gap-x-2 gap-y-1 items-center"
            style={{ transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
          >
            <span>Read Documentation</span>
            <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </div>
      
      {/* Footer Element */}
      <div className="absolute bottom-8 left-0 right-0 text-center pointer-events-none opacity-40">
        <p className="text-neutral-600 text-xs font-mono uppercase tracking-widest">
            Silker AI • 404
        </p>
      </div>
    </main>
  );
}
